import { useMemo } from 'react';
import { useCrmUrgentTaskCount } from '../../../api/useCrm';
import { useCrmPendingIntakesCount } from '../../../api/useCrmSalon';
import { useNootaMeetingCounts } from '../../../api/useNootaMeetings';
import { useCrmCalendarEventCounts } from '../../../api/useCrmCalendarEvents';
import { useCrmEmailCounts } from '../../../api/useCrmEmails';
import { useBlogReviewsPendingCount } from '../../../api/superAdmin/blog/useBlogReviews';
import { useSupportSummary } from '../../../api/superAdmin/support/useSupportAdmin';
import { SupportSummary } from '../../../api/superAdmin/support/types';
import { useProspectingCounts } from '../../../api/useCrmProspectingTracking';
import { useLemcalMeetingCounts } from '../../../api/useCrmProspectingReplies';
import { useStateContext } from '../../../contexts/ContextProvider';
import { StaffAccess, canAccess } from './staffAccess';

export type StaffQueueKey = 'tasks' | 'demandes' | 'noota' | 'calendar' | 'emails' | 'lemcal' | 'blog' | 'support' | 'prospection';

export interface StaffQueue {
    key: StaffQueueKey;
    /** Libellé de la tuile du tableau de bord. */
    label: string;
    /** Libellé de l'onglet « À classer », pour les files qui y figurent. */
    tabLabel?: string;
    link: string;
    access: StaffAccess;
}

/**
 * Files de travail du back-office : la pastille du menu et la tuile du tableau
 * de bord lisent ce même registre, donc le même chiffre.
 *
 * Ajouter une file = une ligne ici + son compteur dans `useStaffQueueCounts`.
 */
export const STAFF_QUEUES: StaffQueue[] = [
    { key: 'tasks', label: 'Tâches urgentes', link: '/super-admin/crm/tasks', access: 'crm:any' },
    // Fiches à suivre sans tâche à venir ni RDV, tous responsables (API `crm:clients`).
    { key: 'demandes', label: 'Demandes à valider', link: '/super-admin/crm/demandes', access: 'crm:prospects' },
    { key: 'noota', label: 'Réunions Noota à classer', tabLabel: 'Réunions Noota', link: '/super-admin/crm/noota-meetings', access: 'crm:any' },
    { key: 'calendar', label: 'RDV agenda à associer', tabLabel: 'Agenda Google', link: '/super-admin/crm/agenda', access: 'crm:any' },
    { key: 'emails', label: 'E-mails à classer', tabLabel: 'E-mails', link: '/super-admin/crm/emails', access: 'crm:any' },
    // API sous `crm/prospecting`, réservée aux super admins (26/10/2026).
    { key: 'lemcal', label: 'RDV Lemcal à rattacher', tabLabel: 'RDV Lemcal', link: '/super-admin/crm/rdv-a-rattacher', access: 'super_admin' },
    { key: 'blog', label: 'Articles à relire', link: '/super-admin/blog', access: 'super_admin' },
    // Conversations de l'assistant support IA passées à l'équipe (API `super_admin`).
    { key: 'support', label: 'Support en attente', link: '/super-admin/support', access: 'super_admin' },
    // Réponses ouvertes + relectures + aperçus bloqués de la prospection à froid (API `super_admin` depuis le 26/10/2026).
    { key: 'prospection', label: 'Prospection à traiter', link: '/super-admin/crm/prospection', access: 'super_admin' },
];

export const STAFF_QUEUE_BY_KEY = Object.fromEntries(
    STAFF_QUEUES.map((queue) => [queue.key, queue]),
) as Record<StaffQueueKey, StaffQueue>;

/** Écrans de tri regroupés sous « À classer », dans l'ordre des onglets. */
export const TO_CLASSIFY_KEYS = ['emails', 'noota', 'calendar', 'lemcal'] as const satisfies readonly StaffQueueKey[];

export type StaffQueueCounts = Partial<Record<StaffQueueKey, number>>;

/** Pastille Support : les conversations en attente de l'équipe, comme l'onglet « File d'attente ». Le manuel a son propre compteur. */
export const supportQueueCount = (summary: SupportSummary): number => summary.waiting_human;

const POLL_MS = 60_000;

/**
 * Compteurs des files accessibles à l'utilisateur.
 *
 * Une file hors des droits n'est JAMAIS interrogée : son API répondrait 403, et
 * ce 403 éjecterait l'utilisateur du back-office, en boucle à chaque
 * rafraîchissement. Une file non chargée est absente du résultat.
 *
 * Seul le layout (menu) sonde : react-query pose un minuteur par observateur,
 * donc chaque lecteur qui sonderait aussi relancerait les mêmes requêtes. Les
 * autres lecteurs (tableau de bord, « À classer ») lisent le cache qu'il tient à jour.
 *
 * @param enabled faux hors mode super admin : rien n'est interrogé.
 * @param options.poll vrai pour le seul lecteur qui rafraîchit les compteurs.
 * @param options.only files à lire (toutes par défaut).
 */
export function useStaffQueueCounts(
    enabled: boolean,
    { poll = false, only }: { poll?: boolean; only?: readonly StaffQueueKey[] } = {},
): StaffQueueCounts {
    const { user } = useStateContext();
    const on = (key: StaffQueueKey) =>
        enabled && (!only || only.includes(key)) && canAccess(user, STAFF_QUEUE_BY_KEY[key].access);
    const interval = poll ? POLL_MS : false;

    const { data: tasks } = useCrmUrgentTaskCount(on('tasks'), interval);
    const { data: demandes } = useCrmPendingIntakesCount(on('demandes'), interval);
    const { data: noota } = useNootaMeetingCounts(on('noota'), interval);
    const { data: calendar } = useCrmCalendarEventCounts(on('calendar'), interval);
    const { data: emails } = useCrmEmailCounts(on('emails'), interval);
    const { data: lemcal } = useLemcalMeetingCounts(on('lemcal'), interval);
    const { data: blog } = useBlogReviewsPendingCount(on('blog'), interval);
    const { data: support } = useSupportSummary(on('support'), interval);
    const { data: prospection } = useProspectingCounts(on('prospection'), interval);

    // Mémoïsé : le menu se reconstruit sur ce seul objet.
    return useMemo(() => {
        const counts: StaffQueueCounts = {};
        if (tasks) counts.tasks = tasks.count;
        if (demandes) counts.demandes = demandes.count;
        // Noota compte par statut et omet un statut vide : pas de clé = 0.
        if (noota) counts.noota = noota.pending ?? 0;
        if (calendar) counts.calendar = calendar.pending ?? 0;
        if (emails) counts.emails = emails.pending;
        if (lemcal) counts.lemcal = lemcal.to_attach;
        // Compté par l'API : une paire dont la décision est déjà en route n'attend plus personne.
        if (blog) counts.blog = blog.count;
        if (support) counts.support = supportQueueCount(support);
        if (prospection) {
            const { replies_open, to_review, previews_stuck } = prospection.data;
            counts.prospection = replies_open + to_review + previews_stuck;
        }
        return counts;
    }, [tasks, demandes, noota, calendar, emails, lemcal, blog, support, prospection]);
}
